import React, { useState, useEffect } from 'react';
import { Tournament } from '../types';
import { apiGet, apiPut } from '../utils/api';

interface BracketTemplate {
  id: string;
  name: string;
  format: string; // SINGLE_ELIMINATION, ROUND_ROBIN, MIXED
  playerCount: number;
  currentStage?: number;
}

interface MatchFormat {
  id: string;
  name: string;
  isDefault: boolean;
}

interface PointsSystemScheme {
  id: string;
  name: string;
  bracketTemplateId?: string | null;
  bracketType?: string;
}

interface TournamentLevelFormatsModalProps {
  tournament: Tournament;
  onClose: () => void;
}

interface LevelBracketConfig {
  templateId: string;
  matchFormatId?: string;
  pointsSchemeId?: string;
  currentStage?: number;
  maxStage?: number;
  preScheduleSkeleton?: boolean;
  hideScheduleMeta?: boolean;
  cancelled?: boolean;
}

const TournamentLevelFormatsModal: React.FC<TournamentLevelFormatsModalProps> = ({ tournament, onClose }) => {
  const [levels, setLevels] = useState<string[]>([]);
  const [bracketTemplates, setBracketTemplates] = useState<BracketTemplate[]>([]);
  const [matchFormats, setMatchFormats] = useState<MatchFormat[]>([]);
  const [pointsSchemes, setPointsSchemes] = useState<PointsSystemScheme[]>([]);
  const [levelBrackets, setLevelBrackets] = useState<Record<string, LevelBracketConfig>>({}); // { "S-4M": { templateId: "xxx", matchFormatId: "yyy", pointsSchemeId: "zzz", currentStage: 1, maxStage: 1, preScheduleSkeleton: true }, ... }
  const [sourceLevelBrackets, setSourceLevelBrackets] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadLevelsAndBrackets();
  }, [tournament.id]);

  // Определяет количество этапов для выбранного шаблона сетки
  const getStagesCount = (template: BracketTemplate | undefined): number => {
    if (!template) return 1;
    
    // Для Mixed формата парсим структуру и считаем этапы
    if (template.format === 'MIXED') {
      try {
        const structure = typeof template.structure === 'string' 
          ? JSON.parse(template.structure)
          : template.structure;
        
        if (structure.stages && Array.isArray(structure.stages)) {
          return structure.stages.length;
        }
      } catch (e) {
        console.error('Ошибка парсинга структуры Mixed:', e);
      }
    }
    
    // Для Single Elimination и Round Robin - один этап
    return 1;
  };

  // Получает название этапа для отображения
  const getStageName = (template: BracketTemplate | undefined, stageNumber: number): string => {
    if (!template) return `Этап ${stageNumber}`;
    
    if (template.format === 'MIXED') {
      try {
        const structure = typeof template.structure === 'string' 
          ? JSON.parse(template.structure)
          : template.structure;
        
        if (structure.stages && Array.isArray(structure.stages)) {
          const stage = structure.stages[stageNumber - 1];
          if (stage && stage.name) {
            return stage.name;
          }
          if (stage && stage.type) {
            const typeName = stage.type === 'ROUND_ROBIN' ? 'Round Robin' : 'Single Elimination';
            return `${typeName} (Этап ${stageNumber})`;
          }
        }
      } catch (e) {
        console.error('Ошибка парсинга структуры Mixed:', e);
      }
    }
    
    return `Этап ${stageNumber}`;
  };

  const loadLevelsAndBrackets = async () => {
    try {
      setLoading(true);
      
      // Загружаем шаблоны сеток, форматы игр, схемы очков и детали турнира параллельно
      const [templatesData, formatsData, schemesData, tournamentDetails] = await Promise.all([
        apiGet<BracketTemplate[]>('/bracket-templates'),
        apiGet<MatchFormat[]>('/match-formats'),
        apiGet<PointsSystemScheme[]>('/points-system'),
        apiGet<any>(`/tournaments/${tournament.id}`)
      ]);
      
      setBracketTemplates(Array.isArray(templatesData) ? templatesData : []);
      setMatchFormats(Array.isArray(formatsData) ? formatsData : []);
      setPointsSchemes(Array.isArray(schemesData) ? schemesData : []);
      
      // Парсим существующие шаблоны сеток по уровням
      let brackets: Record<string, LevelBracketConfig> = {};
      let parsedSourceBrackets: Record<string, any> = {};
      if (tournamentDetails.levelBrackets) {
        try {
          const parsed = typeof tournamentDetails.levelBrackets === 'string' 
            ? JSON.parse(tournamentDetails.levelBrackets)
            : tournamentDetails.levelBrackets;
          parsedSourceBrackets = parsed && typeof parsed === 'object' ? parsed : {};
          
          // Преобразуем старый формат (просто templateId) в новый формат (объект)
          Object.entries(parsed).forEach(([level, value]) => {
            if (typeof value === 'string') {
              // Старый формат: просто templateId
              brackets[level] = {
                templateId: value,
                pointsSchemeId: '',
                currentStage: 1,
                maxStage: 1,
                preScheduleSkeleton: false,
                hideScheduleMeta: false,
                cancelled: false
              };
            } else if (typeof value === 'object' && value !== null) {
              // Новый формат: объект с templateId, matchFormatId, pointsSchemeId, currentStage и maxStage
              brackets[level] = {
                templateId: (value as any).templateId || '',
                matchFormatId: (value as any).matchFormatId || '',
                pointsSchemeId: (value as any).pointsSchemeId || '',
                currentStage: (value as any).currentStage || 1,
                maxStage: (value as any).maxStage || 1,
                preScheduleSkeleton: (value as any).preScheduleSkeleton === true,
                hideScheduleMeta: (value as any).hideScheduleMeta === true,
                cancelled: (value as any).cancelled === true
              };
            }
          });
        } catch (e) {
          console.error('Ошибка парсинга levelBrackets:', e);
        }
      }
      
      // Получаем уникальные уровни из регистраций игроков
      const memberships = tournamentDetails.memberships || [];
      const uniqueLevels = Array.from(new Set(
        memberships
          .filter((m: any) => m.level)
          .map((m: any) => m.level)
      )).sort() as string[];
      
      setLevels(uniqueLevels);
      setLevelBrackets(brackets);
      setSourceLevelBrackets(parsedSourceBrackets);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      alert('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleBracketChange = (level: string, templateId: string) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    const newTemplate = bracketTemplates.find(t => t.id === templateId);
    const stagesCount = getStagesCount(newTemplate);
    const selectedScheme = pointsSchemes.find(s => s.id === currentValue.pointsSchemeId);
    
    // Если текущий этап больше доступных, сбрасываем на 1
    const newCurrentStage = (currentValue.currentStage && currentValue.currentStage <= stagesCount) 
      ? currentValue.currentStage 
      : 1;
    
    // Если максимальный этап больше доступных, сбрасываем на 1
    const newMaxStage = (currentValue.maxStage && currentValue.maxStage <= stagesCount) 
      ? currentValue.maxStage 
      : 1;

    // Оставляем систему очков только если она соответствует выбранной сетке
    const isSameTemplate = !!selectedScheme?.bracketTemplateId && selectedScheme.bracketTemplateId === templateId;
    const isLegacySameType =
      !selectedScheme?.bracketTemplateId &&
      !!selectedScheme?.bracketType &&
      selectedScheme.bracketType === newTemplate?.format;
    const nextPointsSchemeId = templateId && (isSameTemplate || isLegacySameType) ? (currentValue.pointsSchemeId || '') : '';
    
    setLevelBrackets({ 
      ...levelBrackets, 
      [level]: { 
        templateId: templateId || '',
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: nextPointsSchemeId,
        currentStage: newCurrentStage,
        maxStage: newMaxStage,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      } 
    });
  };

  const handleMatchFormatChange = (level: string, matchFormatId: string) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({ 
      ...levelBrackets, 
      [level]: { 
        templateId: currentValue.templateId,
        matchFormatId: matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      } 
    });
  };

  const handlePointsSchemeChange = (level: string, pointsSchemeId: string) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({
      ...levelBrackets,
      [level]: {
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      }
    });
  };

  const handleCurrentStageChange = (level: string, currentStage: number) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({ 
      ...levelBrackets, 
      [level]: { 
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      } 
    });
  };

  const handleMaxStageChange = (level: string, maxStage: number) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({ 
      ...levelBrackets, 
      [level]: { 
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      } 
    });
  };

  const handlePreScheduleSkeletonToggle = (level: string, enabled: boolean) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({
      ...levelBrackets,
      [level]: {
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: enabled,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: currentValue.cancelled === true
      }
    });
  };

  const handleHideScheduleMetaToggle = (level: string, enabled: boolean) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({
      ...levelBrackets,
      [level]: {
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: enabled,
        cancelled: currentValue.cancelled === true
      }
    });
  };

  const handleCancelledToggle = (level: string, enabled: boolean) => {
    const currentValue = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
    setLevelBrackets({
      ...levelBrackets,
      [level]: {
        templateId: currentValue.templateId,
        matchFormatId: currentValue.matchFormatId || '',
        pointsSchemeId: currentValue.pointsSchemeId || '',
        currentStage: currentValue.currentStage || 1,
        maxStage: currentValue.maxStage || 1,
        preScheduleSkeleton: currentValue.preScheduleSkeleton === true,
        hideScheduleMeta: currentValue.hideScheduleMeta === true,
        cancelled: enabled
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Сохраняем шаблоны сеток, не теряя сторонние поля (например registrationEnabled/registrationMaxPlayers)
      const defaultFormat = matchFormats.find(f => f.isDefault);
      const bracketsToSave: Record<string, any> = { ...sourceLevelBrackets };
      Object.entries(levelBrackets).forEach(([level, value]) => {
        const hasTemplate = !!(value.templateId && value.templateId.trim() !== '');
        const isCancelled = value.cancelled === true;
        const existingRaw = bracketsToSave[level];
        const existingObj =
          existingRaw && typeof existingRaw === 'object'
            ? { ...existingRaw }
            : (typeof existingRaw === 'string' ? { templateId: existingRaw } : {});

        if (!hasTemplate && !isCancelled) {
          delete existingObj.templateId;
          delete existingObj.matchFormatId;
          delete existingObj.pointsSchemeId;
          delete existingObj.currentStage;
          delete existingObj.maxStage;
          delete existingObj.preScheduleSkeleton;
          delete existingObj.hideScheduleMeta;
          delete existingObj.cancelled;
        } else {
          const matchFormatId = value.matchFormatId || defaultFormat?.id || '';
          existingObj.templateId = value.templateId || '';
          existingObj.currentStage = value.currentStage || 1;
          existingObj.maxStage = value.maxStage || 1;

          if (matchFormatId) existingObj.matchFormatId = matchFormatId;
          else delete existingObj.matchFormatId;

          if (value.pointsSchemeId) existingObj.pointsSchemeId = value.pointsSchemeId;
          else delete existingObj.pointsSchemeId;

          if (value.preScheduleSkeleton) existingObj.preScheduleSkeleton = true;
          else delete existingObj.preScheduleSkeleton;

          if (value.hideScheduleMeta) existingObj.hideScheduleMeta = true;
          else delete existingObj.hideScheduleMeta;

          if (isCancelled) existingObj.cancelled = true;
          else delete existingObj.cancelled;
        }

        if (Object.keys(existingObj).length > 0) {
          bracketsToSave[level] = existingObj;
        } else {
          delete bracketsToSave[level];
        }
      });
      
      // Если есть хотя бы один шаблон, сохраняем JSON, иначе null
      const levelBracketsJson = Object.keys(bracketsToSave).length > 0 
        ? JSON.stringify(bracketsToSave)
        : null;
      
      console.log('Отправка данных:', { levelBrackets: levelBracketsJson });
      
      await apiPut(`/tournaments/${tournament.id}`, {
        levelBrackets: levelBracketsJson
      });
      
      onClose();
    } catch (error: any) {
      console.error('Ошибка сохранения:', error);
      const errorMessage = error.message || 'Неизвестная ошибка';
      alert(`Ошибка сохранения: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="text-gray-500">Загрузка...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          Типы сеток по уровням
        </h2>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Турнир: <strong>{tournament.name}</strong>
          </p>
          <p className="text-xs text-gray-500">
            Выберите тип сетки (Round Robin, Single Elimination, Mixed) и формат игры для каждого уровня турнира
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Формат (одиночный/парный) определяется в настройках турнира
          </p>
        </div>

        {levels.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Нет зарегистрированных игроков</p>
            <p className="text-xs mt-2">Зарегистрируйте игроков, чтобы настроить типы сеток</p>
          </div>
        ) : (
          <div className="space-y-3">
            {levels.map(level => {
              const levelData = levelBrackets[level] || { templateId: '', matchFormatId: '', pointsSchemeId: '', currentStage: 1, maxStage: 1, preScheduleSkeleton: false, hideScheduleMeta: false, cancelled: false };
              const selectedTemplate = bracketTemplates.find(t => t.id === levelData.templateId);
              const selectedMatchFormat = matchFormats.find(f => f.id === levelData.matchFormatId);
              const defaultMatchFormat = matchFormats.find(f => f.isDefault);
              const filteredPointsSchemes = pointsSchemes.filter((scheme) => {
                if (!selectedTemplate) return false;
                // Строго совместимые схемы: либо точное совпадение выбранного шаблона,
                // либо legacy-схемы без templateId, где совпадает только общий тип сетки.
                if (scheme.bracketTemplateId) {
                  return scheme.bracketTemplateId === selectedTemplate.id;
                }
                if (scheme.bracketType) {
                  return scheme.bracketType === selectedTemplate.format;
                }
                return false;
              });
              const formatName = selectedTemplate?.format === 'ROUND_ROBIN' ? 'Round Robin' :
                                selectedTemplate?.format === 'SINGLE_ELIMINATION' ? 'Single Elimination' :
                                selectedTemplate?.format === 'MIXED' ? 'Mixed' : '';
              
              return (
                <div key={level} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xl font-semibold text-gray-700">
                      Уровень: <span className="text-blue-600">{level}</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={levelData.cancelled === true}
                          onChange={(e) => handleCancelledToggle(level, e.target.checked)}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        Отменить
                      </label>
                      {selectedTemplate && (
                        <span className="text-xs text-gray-500">
                          {formatName} ({selectedTemplate.playerCount} игроков)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Тип сетки
                    </label>
                    <select
                      value={levelData.templateId || ''}
                      onChange={(e) => handleBracketChange(level, e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Не выбрано</option>
                      {bracketTemplates
                        .filter(template => {
                          // Фильтруем по формату турнира (если задан)
                          if (tournament.format) {
                            // Для SINGLES показываем только шаблоны для одиночных
                            // Для DOUBLES - только для парных
                            // Но сейчас у шаблонов нет такого поля, поэтому показываем все
                            return true;
                          }
                          return true;
                        })
                        .map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name} ({template.format === 'ROUND_ROBIN' ? 'Round Robin' : 
                                             template.format === 'SINGLE_ELIMINATION' ? 'Single Elimination' : 
                                             template.format === 'MIXED' ? 'Mixed' : template.format}, {template.playerCount} игроков)
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Система начисления очков (опционально)
                    </label>
                    <select
                      value={levelData.pointsSchemeId || ''}
                      onChange={(e) => handlePointsSchemeChange(level, e.target.value)}
                      disabled={!selectedTemplate}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">Не выбрано</option>
                      {filteredPointsSchemes.map((scheme) => (
                        <option key={scheme.id} value={scheme.id}>
                          {scheme.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Показываются только схемы для выбранного типа сетки
                    </p>
                  </div>

                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Формат игры
                    </label>
                    <select
                      value={levelData.matchFormatId || (defaultMatchFormat?.id || '')}
                      onChange={(e) => handleMatchFormatChange(level, e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {matchFormats.map(format => (
                        <option key={format.id} value={format.id}>
                          {format.name} {format.isDefault ? '(по умолчанию)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Правила ведения счета для матчей этого уровня
                    </p>
                  </div>
                  <div className="mb-2 p-2 border border-blue-100 bg-blue-50 rounded-lg">
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={levelData.preScheduleSkeleton === true}
                        onChange={(e) => handlePreScheduleSkeletonToggle(level, e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="text-xs font-medium text-gray-800 block">
                          Разрешить предрасписание сетки (создавать каркас X/X)
                        </span>
                        <span className="text-xs text-gray-600">
                          Позволяет заранее назначать корт/время матчам с неизвестными участниками через Court Reservations.
                        </span>
                      </span>
                    </label>
                  </div>
                  <div className="mb-2 p-2 border border-gray-200 bg-gray-50 rounded-lg">
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={levelData.hideScheduleMeta === true}
                        onChange={(e) => handleHideScheduleMetaToggle(level, e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="text-xs font-medium text-gray-800 block">
                          Не выводить дату/время/корт
                        </span>
                        <span className="text-xs text-gray-600">
                          Если включено, в турнирной таблице над матчами не показывается подпись с датой, временем и кортом.
                        </span>
                      </span>
                    </label>
                  </div>
                  {selectedTemplate && (
                    <>
                      <div className="flex gap-3 mb-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Текущий этап
                          </label>
                          <select
                            value={levelData.currentStage || 1}
                            onChange={(e) => handleCurrentStageChange(level, parseInt(e.target.value) || 1)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            {Array.from({ length: getStagesCount(selectedTemplate) }, (_, i) => i + 1).map(stageNum => (
                              <option key={stageNum} value={stageNum}>
                                {getStageName(selectedTemplate, stageNum)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Выберите текущий этап из структуры сетки
                          </p>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Максимальный выводимый этап
                          </label>
                          <select
                            value={levelData.maxStage || 1}
                            onChange={(e) => handleMaxStageChange(level, parseInt(e.target.value) || 1)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            {Array.from({ length: getStagesCount(selectedTemplate) }, (_, i) => i + 1).map(stageNum => (
                              <option key={stageNum} value={stageNum}>
                                {getStageName(selectedTemplate, stageNum)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Максимальный этап для отображения в таблице
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedTemplate.description || 'Описание отсутствует'}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex space-x-3 pt-6 mt-6 border-t">
          <button
            onClick={handleSave}
            disabled={saving || levels.length === 0}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 disabled:bg-gray-200"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentLevelFormatsModal;

